import React from 'react';
import { Product } from '../../types';
import { formatBRL } from '../../utils/format';
import { ShoppingBag, ArrowUpRight, Sparkles } from 'lucide-react';
import { useCart } from '../../context/CartContext';

interface ProductCardProps {
  product: Product;
  onNavigate: (path: string) => void;
}

export function ProductCard({ product, onNavigate }: ProductCardProps) {
  const { addToCart } = useCart();

  const handleQuickAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (product.variants && product.variants.length > 0) {
      await addToCart(product.variants[0].id, 1);
    } else {
      onNavigate(`/produto/${product.slug}`);
    }
  };

  const getBadgeLabel = () => {
    if (product.product_type === 'digital') return 'Download Digital';
    if (product.product_type === 'service') return 'Serviço Sob Medida';
    if (product.is_featured) return 'Destaque do Ateliê';
    return null;
  };

  const badge = getBadgeLabel();

  return (
    <div
      onClick={() => onNavigate(`/produto/${product.slug}`)}
      className="group cursor-pointer flex flex-col bg-surface border border-border-subtle hover:border-ink/20 rounded-xs transition-all duration-200 overflow-hidden"
    >
      {/* Product Image Container with 4:5 Aspect Ratio */}
      <div className="relative aspect-[4/5] bg-background-alt overflow-hidden">
        <img
          src={product.cover_image || 'https://images.unsplash.com/photo-1612196808214-b8e1d6145a8c?auto=format&fit=crop&w=800&q=85'}
          alt={product.cover_alt || product.title}
          loading="lazy"
          className="w-full h-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.03]"
        />

        {/* Badge */}
        {badge && (
          <div className="absolute top-3 left-3 bg-surface/90 backdrop-blur-xs px-2.5 py-1 rounded-xs border border-border-subtle text-[10px] uppercase tracking-wider font-semibold text-ink flex items-center gap-1">
            {product.product_type === 'digital' && <Sparkles className="w-3 h-3 text-primary" />}
            {badge}
          </div>
        )}

        {/* Quick Add Overlay on Hover for Desktop */}
        <div className="absolute inset-x-3 bottom-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden sm:block">
          <button
            onClick={handleQuickAdd}
            className="w-full bg-surface/95 backdrop-blur-xs text-ink py-2.5 px-3 rounded-xs text-xs uppercase tracking-wider font-medium hover:bg-primary hover:text-white transition-colors border border-border-subtle flex items-center justify-center gap-2 shadow-subtle"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            Adicionar à Sacola
          </button>
        </div>
      </div>

      {/* Product Details */}
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <h3 className="font-serif text-base sm:text-lg font-medium text-ink group-hover:text-primary transition-colors line-clamp-1">
            {product.title}
          </h3>
          <p className="text-xs text-ink-muted mt-1 line-clamp-2 leading-relaxed">
            {product.summary}
          </p>
        </div>

        <div className="mt-4 pt-3 border-t border-border-subtle flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-mono font-medium text-ink">
              {formatBRL(product.base_price_cents)}
            </span>
            {product.compare_at_price_cents && product.compare_at_price_cents > product.base_price_cents && (
              <span className="text-xs font-mono text-ink-muted line-through">
                {formatBRL(product.compare_at_price_cents)}
              </span>
            )}
          </div>

          <span className="text-xs text-primary font-medium flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            Ver detalhes <ArrowUpRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </div>
  );
}
